/**
 * Coffee Bean Clicker v2 - Main Game View Controller
 * Subscribes to GameCore state and renders via PixiJS
 */

import * as PIXI from 'pixi.js';
import type { GameCore } from '@app-games/core';
import type { GameState, GameAction } from '../game/index.js';
import {
  computeTapPower, computeBPS,
  computeOfflineEarnings, getComboTier,
  formatNumber, formatTime,
} from '../game/index.js';
import { STAGES, ROAST_ZONES } from '../game/data/upgrades.js';
import { t } from '../i18n/index.js';
import { THEME, LAYOUT } from './ui/theme.js';
import {
  drawGlassCard, drawPillButton, drawProgressBar,
  drawHeatGauge, makeText,
} from './ui/UIHelpers.js';
import { PopupManager } from './ui/PopupManager.js';
import { ProducerPanel } from './ui/ProducerPanel.js';
import { AchievementPanel } from './ui/AchievementPanel.js';
import { PrestigePanel } from './ui/PrestigePanel.js';
import { SettingsPanel } from './ui/SettingsPanel.js';

// ── Tab definitions ──
const TAB_EMOJIS = ['\u2615', '\u26A1', '\ud83c\udfc5', '\u267B\uFE0F', '\u2699\uFE0F'];

export class GameView {
  private app: PIXI.Application;
  private core: GameCore<GameState, GameAction>;
  private screenW = 0;
  private screenH = 0;

  // ── Layers ──
  private bgLayer = new PIXI.Container();
  private contentLayer = new PIXI.Container();
  private overlayLayer = new PIXI.Container();

  // ── Background ──
  private bgSprite: PIXI.Sprite | null = null;
  private bgOverlay = new PIXI.Graphics();

  // ── Top bar ──
  private topBarGfx = new PIXI.Graphics();
  private beanCountText!: PIXI.Text;
  private bpsText!: PIXI.Text;
  private goldenBeanText!: PIXI.Text;
  private stageBadgeGfx = new PIXI.Graphics();
  private stageBadgeText!: PIXI.Text;

  // ── Tap area ──
  private tapContainer = new PIXI.Container();
  private beanSprite: PIXI.Sprite | null = null;
  private beanBaseScale = 1;
  private tapHintText!: PIXI.Text;

  // ── Combo ──
  private comboContainer = new PIXI.Container();
  private comboCountText!: PIXI.Text;
  private comboTierText!: PIXI.Text;
  private comboBarGfx = new PIXI.Graphics();

  // ── Boost buttons ──
  private boostContainer = new PIXI.Container();
  private tapBoostGfx = new PIXI.Graphics();
  private tapBoostText!: PIXI.Text;
  private bpsBoostGfx = new PIXI.Graphics();
  private bpsBoostText!: PIXI.Text;

  // ── Tab bar ──
  private tabBarGfx = new PIXI.Graphics();
  private tabTexts: PIXI.Text[] = [];
  private tabIndicatorGfx = new PIXI.Graphics();
  private activeTab = 0;

  // ── Panels ──
  private producerPanel!: ProducerPanel;
  private achievementPanel!: AchievementPanel;
  private prestigePanel!: PrestigePanel;
  private settingsPanel!: SettingsPanel;
  private panelContainer = new PIXI.Container();

  // ── Golden cherry ──
  private cherrySprite: PIXI.Sprite | null = null;
  private cherryContainer = new PIXI.Container();
  private cherryBounceTime = 0;

  // ── Roasting overlay ──
  private roastOverlay = new PIXI.Container();
  private roastGaugeGfx = new PIXI.Graphics();
  private roastLabelText!: PIXI.Text;
  private roastResultContainer = new PIXI.Container();
  private roastResultText!: PIXI.Text;
  private roastRewardText!: PIXI.Text;
  private roastCollectGfx = new PIXI.Graphics();
  private roastCollectText!: PIXI.Text;

  // ── Popup / Toast ──
  private popupManager!: PopupManager;

  // ── Offline popup ──
  private offlineOverlay = new PIXI.Container();

  // ── State tracking ──
  private prevAchievementCount = 0;
  private showMainView = true;

  constructor(app: PIXI.Application, core: GameCore<GameState, GameAction>) {
    this.app = app;
    this.core = core;
    this.screenW = app.screen.width;
    this.screenH = app.screen.height;

    this.setupLayers();
    this.setupBackground();
    this.setupTopBar();
    this.setupTapArea();
    this.setupCombo();
    this.setupBoostButtons();
    this.setupTabBar();
    this.setupPanels();
    this.setupGoldenCherry();
    this.setupRoastingOverlay();
    this.setupOfflinePopup();

    this.popupManager = new PopupManager(this.overlayLayer, this.screenW);

    // Inject CSS keyframes for floating text
    if (!document.getElementById('float-text-style')) {
      const style = document.createElement('style');
      style.id = 'float-text-style';
      style.textContent = `
        @keyframes floatUp {
          0% { transform: translateY(0); opacity: 1; }
          70% { opacity: 1; }
          100% { transform: translateY(-80px); opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }

    // Subscribe to state changes for achievement toasts
    this.core.on('stateChanged', (state, action) => {
      if (action.type === 'CHECK_ACHIEVEMENTS') {
        const newCount = state.unlockedAchievements.length;
        if (newCount > this.prevAchievementCount) {
          // Show toast for new achievements
          const diff = newCount - this.prevAchievementCount;
          this.popupManager.showAchievement(
            `+${diff} ${t('tab.achievements')}`
          );
        }
        this.prevAchievementCount = newCount;
      }
    });

    const initState = this.core.getState();
    this.prevAchievementCount = initState.unlockedAchievements.length;

    // Check offline earnings on startup
    this.checkOfflineEarnings();

    this.resize();
  }

  // ═══════════════════════════════════════════════
  //  Setup Methods
  // ═══════════════════════════════════════════════

  private setupLayers(): void {
    this.app.stage.addChild(this.bgLayer);
    this.app.stage.addChild(this.contentLayer);
    this.app.stage.addChild(this.overlayLayer);
  }

  private setupBackground(): void {
    // Try to load background image
    const bgTexture = PIXI.Assets.get('farm-bg');
    if (bgTexture) {
      this.bgSprite = new PIXI.Sprite(bgTexture);
      this.bgLayer.addChild(this.bgSprite);
    }
    // Dark overlay on top of bg
    this.bgLayer.addChild(this.bgOverlay);
  }

  private setupTopBar(): void {
    this.contentLayer.addChild(this.topBarGfx);

    this.beanCountText = makeText('0', 28, THEME.text.primary, { fontWeight: 'bold' });
    this.beanCountText.anchor.set(0, 0.5);
    this.contentLayer.addChild(this.beanCountText);

    this.bpsText = makeText('0/s', 13, THEME.text.secondary);
    this.bpsText.anchor.set(0, 0.5);
    this.contentLayer.addChild(this.bpsText);

    this.goldenBeanText = makeText('', 13, THEME.text.gold);
    this.goldenBeanText.anchor.set(1, 0.5);
    this.contentLayer.addChild(this.goldenBeanText);

    this.contentLayer.addChild(this.stageBadgeGfx);
    this.stageBadgeText = makeText('', 11, THEME.text.white, { fontWeight: 'bold' });
    this.stageBadgeText.anchor.set(0.5, 0.5);
    this.contentLayer.addChild(this.stageBadgeText);
  }

  private setupTapArea(): void {
    this.tapContainer.eventMode = 'static';
    this.tapContainer.cursor = 'pointer';
    this.tapContainer.on('pointerdown', (e: PIXI.FederatedPointerEvent) => {
      this.handleTap(e);
    });

    // Bean icon sprite
    const beanTexture = PIXI.Assets.get('bean-icon');
    if (beanTexture) {
      this.beanSprite = new PIXI.Sprite(beanTexture);
      this.beanSprite.anchor.set(0.5);
      this.tapContainer.addChild(this.beanSprite);
    } else {
      // Fallback circle
      const fallback = new PIXI.Graphics();
      fallback.beginFill(THEME.accent.brown, 0.6);
      fallback.drawCircle(0, 0, 60);
      fallback.endFill();
      this.tapContainer.addChild(fallback);
    }

    this.tapHintText = makeText(t('header.tap'), 16, THEME.text.secondary);
    this.tapHintText.anchor.set(0.5, 0);
    this.tapContainer.addChild(this.tapHintText);

    this.contentLayer.addChild(this.tapContainer);
  }

  private setupCombo(): void {
    this.comboCountText = makeText('', 20, THEME.text.combo, { fontWeight: 'bold' });
    this.comboCountText.anchor.set(0, 0.5);
    this.comboContainer.addChild(this.comboCountText);

    this.comboTierText = makeText('', 14, THEME.text.combo);
    this.comboTierText.anchor.set(0, 0.5);
    this.comboContainer.addChild(this.comboTierText);

    this.comboContainer.addChild(this.comboBarGfx);

    this.contentLayer.addChild(this.comboContainer);
  }

  private setupBoostButtons(): void {
    // Tap boost button
    this.tapBoostGfx.eventMode = 'static';
    this.tapBoostGfx.cursor = 'pointer';
    this.tapBoostGfx.on('pointerdown', () => {
      this.core.dispatch({
        type: 'ACTIVATE_BOOST',
        payload: { target: 'tap', multiplier: 3, durationSec: 30 },
      });
      this.core.dispatch({ type: 'RECORD_AD_WATCHED' });
    });
    this.boostContainer.addChild(this.tapBoostGfx);

    this.tapBoostText = makeText(
      `\uD83D\uDE80 ${t('boost.tap')}`, 12, THEME.text.white, { fontWeight: 'bold' }
    );
    this.tapBoostText.anchor.set(0.5, 0.5);
    this.boostContainer.addChild(this.tapBoostText);

    // BPS boost button
    this.bpsBoostGfx.eventMode = 'static';
    this.bpsBoostGfx.cursor = 'pointer';
    this.bpsBoostGfx.on('pointerdown', () => {
      this.core.dispatch({
        type: 'ACTIVATE_BOOST',
        payload: { target: 'bps', multiplier: 3, durationSec: 30 },
      });
      this.core.dispatch({ type: 'RECORD_AD_WATCHED' });
    });
    this.boostContainer.addChild(this.bpsBoostGfx);

    this.bpsBoostText = makeText(
      `\uD83D\uDCFA ${t('boost.bps')}`, 12, THEME.text.white, { fontWeight: 'bold' }
    );
    this.bpsBoostText.anchor.set(0.5, 0.5);
    this.boostContainer.addChild(this.bpsBoostText);

    this.contentLayer.addChild(this.boostContainer);
  }

  private setupTabBar(): void {
    this.contentLayer.addChild(this.tabBarGfx);
    this.contentLayer.addChild(this.tabIndicatorGfx);

    for (let i = 0; i < TAB_EMOJIS.length; i++) {
      const txt = makeText(TAB_EMOJIS[i], 22, THEME.text.secondary);
      txt.anchor.set(0.5, 0.5);
      txt.eventMode = 'static';
      txt.cursor = 'pointer';
      txt.on('pointerdown', () => this.switchTab(i));
      this.tabTexts.push(txt);
      this.contentLayer.addChild(txt);
    }
  }

  private setupPanels(): void {
    const pw = this.screenW;
    const ph = this.getPanelHeight();

    this.producerPanel = new ProducerPanel(pw, ph, this.core);
    this.achievementPanel = new AchievementPanel(pw, ph, this.core);
    this.prestigePanel = new PrestigePanel(pw, ph, this.core);
    this.settingsPanel = new SettingsPanel(pw, ph, this.core);

    this.panelContainer.addChild(this.producerPanel);
    this.panelContainer.addChild(this.achievementPanel);
    this.panelContainer.addChild(this.prestigePanel);
    this.panelContainer.addChild(this.settingsPanel);

    this.contentLayer.addChild(this.panelContainer);

    // Initial visibility
    this.switchTab(0);
  }

  private setupGoldenCherry(): void {
    const cherryTexture = PIXI.Assets.get('golden-cherry');
    if (cherryTexture) {
      this.cherrySprite = new PIXI.Sprite(cherryTexture);
      this.cherrySprite.anchor.set(0.5);
      this.cherrySprite.width = 56;
      this.cherrySprite.height = 56;
      this.cherryContainer.addChild(this.cherrySprite);
    } else {
      const gfx = new PIXI.Graphics();
      gfx.beginFill(THEME.accent.cherry, 0.9);
      gfx.drawCircle(0, 0, 28);
      gfx.endFill();
      this.cherryContainer.addChild(gfx);
      const label = makeText('\uD83C\uDF52', 28, THEME.text.white);
      label.anchor.set(0.5);
      this.cherryContainer.addChild(label);
    }

    this.cherryContainer.eventMode = 'static';
    this.cherryContainer.cursor = 'pointer';
    this.cherryContainer.on('pointerdown', () => {
      this.core.dispatch({ type: 'TAP_GOLDEN_CHERRY' });
    });
    this.cherryContainer.visible = false;

    this.overlayLayer.addChild(this.cherryContainer);
  }

  private setupRoastingOverlay(): void {
    this.roastOverlay.visible = false;

    // Dim background
    const dimBg = new PIXI.Graphics();
    dimBg.beginFill(0x000000, 0.7);
    dimBg.drawRect(0, 0, this.screenW, this.screenH);
    dimBg.endFill();
    dimBg.eventMode = 'static'; // block tap-through
    this.roastOverlay.addChild(dimBg);

    // Gauge
    this.roastOverlay.addChild(this.roastGaugeGfx);

    // Tap label
    this.roastLabelText = makeText(t('roast.tap'), 18, THEME.text.white, { fontWeight: 'bold' });
    this.roastLabelText.anchor.set(0.5);
    this.roastOverlay.addChild(this.roastLabelText);

    // Tap to stop
    const tapArea = new PIXI.Graphics();
    tapArea.beginFill(0x000000, 0.001);
    tapArea.drawRect(0, 0, this.screenW, this.screenH);
    tapArea.endFill();
    tapArea.eventMode = 'static';
    tapArea.on('pointerdown', () => {
      const st = this.core.getState();
      if (st.roasting.active && st.roasting.result === null) {
        this.core.dispatch({ type: 'STOP_ROASTING' });
      }
    });
    this.roastOverlay.addChild(tapArea);

    // Result container (shown after stop)
    this.roastResultText = makeText('', 22, THEME.text.gold, { fontWeight: 'bold' });
    this.roastResultText.anchor.set(0.5);
    this.roastResultContainer.addChild(this.roastResultText);

    this.roastRewardText = makeText('', 16, THEME.accent.cream);
    this.roastRewardText.anchor.set(0.5);
    this.roastResultContainer.addChild(this.roastRewardText);

    // Collect button
    this.roastCollectGfx.eventMode = 'static';
    this.roastCollectGfx.cursor = 'pointer';
    this.roastCollectGfx.on('pointerdown', () => {
      this.core.dispatch({ type: 'COLLECT_ROAST' });
    });
    this.roastResultContainer.addChild(this.roastCollectGfx);

    this.roastCollectText = makeText(t('roast.collect'), 14, THEME.text.white, { fontWeight: 'bold' });
    this.roastCollectText.anchor.set(0.5, 0.5);
    this.roastResultContainer.addChild(this.roastCollectText);

    this.roastResultContainer.visible = false;
    this.roastOverlay.addChild(this.roastResultContainer);

    this.overlayLayer.addChild(this.roastOverlay);
  }

  private setupOfflinePopup(): void {
    this.offlineOverlay.visible = false;
    this.overlayLayer.addChild(this.offlineOverlay);
  }

  // ═══════════════════════════════════════════════
  //  Offline earnings check
  // ═══════════════════════════════════════════════

  private checkOfflineEarnings(): void {
    const state = this.core.getState();
    const now = Date.now();
    const elapsed = (now - state.lastSaveTimestamp) / 1000;
    if (elapsed < 60) return; // skip if less than 1 minute

    const earnings = computeOfflineEarnings(state, elapsed);
    if (earnings <= 0) return;

    this.showOfflinePopup(earnings, elapsed);
  }

  private showOfflinePopup(earnings: number, seconds: number): void {
    this.offlineOverlay.removeChildren();

    // Dim bg
    const dim = new PIXI.Graphics();
    dim.beginFill(0x000000, 0.75);
    dim.drawRect(0, 0, this.screenW, this.screenH);
    dim.endFill();
    dim.eventMode = 'static';
    this.offlineOverlay.addChild(dim);

    const cardW = Math.min(this.screenW - 40, 320);
    const cardH = 220;
    const cx = (this.screenW - cardW) / 2;
    const cy = (this.screenH - cardH) / 2;

    const cardGfx = new PIXI.Graphics();
    drawGlassCard(cardGfx, cx, cy, cardW, cardH, LAYOUT.cardRadius, 0.15);
    this.offlineOverlay.addChild(cardGfx);

    const titleTxt = makeText(t('offline.title'), 20, THEME.text.primary, { fontWeight: 'bold' });
    titleTxt.anchor.set(0.5, 0);
    titleTxt.x = this.screenW / 2;
    titleTxt.y = cy + 16;
    this.offlineOverlay.addChild(titleTxt);

    const timeTxt = makeText(
      `${t('offline.time')}: ${formatTime(seconds)}`,
      13, THEME.text.secondary
    );
    timeTxt.anchor.set(0.5, 0);
    timeTxt.x = this.screenW / 2;
    timeTxt.y = cy + 48;
    this.offlineOverlay.addChild(timeTxt);

    const earnTxt = makeText(
      `${t('offline.earned')}: ${formatNumber(earnings)}`,
      18, THEME.text.gold, { fontWeight: 'bold' }
    );
    earnTxt.anchor.set(0.5, 0);
    earnTxt.x = this.screenW / 2;
    earnTxt.y = cy + 76;
    this.offlineOverlay.addChild(earnTxt);

    // Collect button
    const btnW = 140;
    const btnH = 40;
    const btnX = (this.screenW - btnW) / 2;
    const btnY = cy + cardH - 60;
    const btnGfx = new PIXI.Graphics();
    drawPillButton(btnGfx, btnX, btnY, btnW, btnH, THEME.button.primary);
    btnGfx.eventMode = 'static';
    btnGfx.cursor = 'pointer';
    btnGfx.on('pointerdown', () => {
      this.core.dispatch({
        type: 'COLLECT_OFFLINE',
        payload: { beans: earnings, seconds },
      });
      this.offlineOverlay.visible = false;
    });
    this.offlineOverlay.addChild(btnGfx);

    const btnLabel = makeText(t('offline.collect'), 14, THEME.text.white, { fontWeight: 'bold' });
    btnLabel.anchor.set(0.5, 0.5);
    btnLabel.x = btnX + btnW / 2;
    btnLabel.y = btnY + btnH / 2;
    this.offlineOverlay.addChild(btnLabel);

    this.offlineOverlay.visible = true;
  }

  // ═══════════════════════════════════════════════
  //  Tap handling
  // ═══════════════════════════════════════════════

  private handleTap(e: PIXI.FederatedPointerEvent): void {
    const prevState = this.core.getState();
    const prevBeans = prevState.beans;
    const result = this.core.dispatch({ type: 'TAP' });
    if (!result.success || !result.stateChanged) return;

    const newState = this.core.getState();
    const earned = newState.beans - prevBeans;
    const isCritical = earned > computeTapPower(prevState) * 2;

    // DOM-based floating text overlay
    this.spawnDOMFloat(`+${formatNumber(earned)}`, e.global.x, e.global.y, isCritical);

    // Tap animation (scale bounce)
    if (this.beanSprite) {
      this.beanSprite.scale.set(this.beanBaseScale * 1.15);
    }
  }

  private spawnDOMFloat(label: string, x: number, y: number, isCritical: boolean): void {
    const el = document.createElement('div');
    el.textContent = label;
    el.style.cssText = [
      'position:fixed',
      `left:${x + (Math.random() - 0.5) * 40}px`,
      `top:${y - 20}px`,
      `color:${isCritical ? '#FFD700' : '#FFFFFF'}`,
      `font-size:${isCritical ? '28px' : '20px'}`,
      'font-weight:bold',
      'font-family:Arial,sans-serif',
      'text-shadow:0 0 4px #000,0 0 4px #000',
      'pointer-events:none',
      'z-index:9999',
      'animation:floatUp 1s ease-out forwards',
    ].join(';');
    document.body.appendChild(el);
    el.addEventListener('animationend', () => el.remove());
  }

  // ═══════════════════════════════════════════════
  //  Tab switching
  // ═══════════════════════════════════════════════

  private switchTab(index: number): void {
    this.activeTab = index;

    // Show main view (tap area + boosts) only on tab 0
    this.showMainView = index === 0;
    this.tapContainer.visible = this.showMainView;
    this.comboContainer.visible = this.showMainView;
    this.boostContainer.visible = this.showMainView;
    // Panel visibility
    this.producerPanel.visible = index === 1;
    this.achievementPanel.visible = index === 2;
    this.prestigePanel.visible = index === 3;
    this.settingsPanel.visible = index === 4;

    this.panelContainer.visible = index > 0;

    // Update tab highlight
    for (let i = 0; i < this.tabTexts.length; i++) {
      this.tabTexts[i].style.fill = i === index ? THEME.text.gold : THEME.text.secondary;
    }

    this.drawTabIndicator();

    // Refresh active panel
    const state = this.core.getState();
    if (index === 1) this.producerPanel.refresh(state);
    if (index === 2) this.achievementPanel.refresh(state);
    if (index === 3) this.prestigePanel.refresh(state);
    if (index === 4) this.settingsPanel.refresh(state);
  }

  private drawTabIndicator(): void {
    this.tabIndicatorGfx.clear();
    const tabW = this.screenW / TAB_EMOJIS.length;
    const ix = this.activeTab * tabW;
    const iy = this.screenH - LAYOUT.tabBarH - LAYOUT.safeBottom;
    this.tabIndicatorGfx.beginFill(THEME.accent.gold, 0.8);
    this.tabIndicatorGfx.drawRoundedRect(ix + tabW * 0.2, iy, tabW * 0.6, 3, 1.5);
    this.tabIndicatorGfx.endFill();
  }

  // ═══════════════════════════════════════════════
  //  Per-frame update
  // ═══════════════════════════════════════════════

  onTick(dt: number): void {
    const state = this.core.getState();

    this.updateTopBar(state);
    this.updateTapArea(state, dt);
    this.updateCombo(state);
    this.updateGoldenCherry(state, dt);
    this.updateRoastingOverlay(state);
    this.updateBoostButtons(state);

    // Update active panel
    if (this.activeTab === 1) this.producerPanel.refresh(state);
    else if (this.activeTab === 2) this.achievementPanel.refresh(state);
    else if (this.activeTab === 3) this.prestigePanel.refresh(state);

    // Scroll update
    if (this.producerPanel.visible) this.producerPanel.updateScroll(dt);
    if (this.achievementPanel.visible) this.achievementPanel.updateScroll(dt);
    if (this.prestigePanel.visible) this.prestigePanel.updateScroll(dt);

    this.popupManager.update(dt);
  }

  private updateTopBar(state: GameState): void {
    this.beanCountText.text = formatNumber(state.beans);
    const bps = computeBPS(state);
    this.bpsText.text = `${formatNumber(bps)}${t('header.bps')}`;

    // Golden beans
    if (state.goldenBeans > 0 || state.starDust > 0) {
      let gbStr = `\u2728 ${formatNumber(state.goldenBeans)}`;
      if (state.starDust > 0) gbStr += `  \u2B50 ${formatNumber(state.starDust)}`;
      this.goldenBeanText.text = gbStr;
    } else {
      this.goldenBeanText.text = '';
    }

    // Stage badge
    const stageDef = STAGES.find(s => s.id === state.currentStage);
    if (stageDef) {
      const label = `${t('header.stage')} ${state.currentStage}`;
      this.stageBadgeText.text = label;
    }
  }

  private updateTapArea(_state: GameState, dt: number): void {
    // Bean sprite bounce-back
    if (this.beanSprite) {
      const s = this.beanSprite.scale.x;
      const base = this.beanBaseScale;
      if (s > base) {
        this.beanSprite.scale.set(Math.max(base, s - dt * base * 3));
      }
    }
  }

  private updateCombo(state: GameState): void {
    const { combo } = state;
    if (combo.count < 2) {
      this.comboContainer.alpha = 0;
      return;
    }

    this.comboContainer.alpha = 1;
    this.comboCountText.text = `x${combo.count}`;

    const tier = getComboTier(combo.count);
    this.comboTierText.text = tier ? t(tier.labelKey) : '';

    // Combo bar
    this.comboBarGfx.clear();
    const barW = 80;
    const barH = 6;
    const progress = combo.timer / combo.maxTimer;
    drawProgressBar(
      this.comboBarGfx, 0, 28, barW, barH, progress,
      0x000000, THEME.text.combo, 0.3
    );
  }

  private updateGoldenCherry(state: GameState, dt: number): void {
    const { goldenCherry } = state;
    this.cherryContainer.visible = goldenCherry.active;

    if (goldenCherry.active) {
      this.cherryBounceTime += dt * 3;
      const cx = this.screenW * 0.5 + Math.sin(this.cherryBounceTime * 1.3) * (this.screenW * 0.3);
      const cy = this.screenH * 0.35 + Math.cos(this.cherryBounceTime) * 40;
      this.cherryContainer.x = cx;
      this.cherryContainer.y = cy;

      // Pulsing scale
      const sc = 1 + Math.sin(this.cherryBounceTime * 4) * 0.1;
      this.cherryContainer.scale.set(sc);
    }
  }

  private updateRoastingOverlay(state: GameState): void {
    const { roasting } = state;
    this.roastOverlay.visible = roasting.active;

    if (!roasting.active) return;

    const gaugeW = 50;
    const gaugeH = Math.min(300, this.screenH * 0.4);
    const gx = (this.screenW - gaugeW) / 2;
    const gy = (this.screenH - gaugeH) / 2 - 20;

    this.roastGaugeGfx.clear();
    drawHeatGauge(this.roastGaugeGfx, gx, gy, gaugeW, gaugeH, roasting.heatLevel);

    this.roastLabelText.x = this.screenW / 2;
    this.roastLabelText.y = gy + gaugeH + 24;

    if (roasting.result !== null) {
      // Show result
      this.roastLabelText.visible = false;
      this.roastResultContainer.visible = true;

      // Find zone label
      let zoneLabelKey = 'roast.raw';
      for (const zone of ROAST_ZONES) {
        if (roasting.heatLevel >= zone.min && roasting.heatLevel < zone.max) {
          zoneLabelKey = zone.labelKey;
          break;
        }
      }

      this.roastResultText.text = t(zoneLabelKey);
      this.roastResultText.x = this.screenW / 2;
      this.roastResultText.y = gy + gaugeH + 30;

      const reward = Math.floor(computeBPS(state) * 30 * roasting.result);
      this.roastRewardText.text = `${t('roast.reward')}: +${formatNumber(Math.max(10, reward))}`;
      this.roastRewardText.x = this.screenW / 2;
      this.roastRewardText.y = gy + gaugeH + 58;

      // Collect button
      const btnW = 120;
      const btnH = 36;
      const btnX = (this.screenW - btnW) / 2;
      const btnY = gy + gaugeH + 84;
      this.roastCollectGfx.clear();
      drawPillButton(this.roastCollectGfx, btnX, btnY, btnW, btnH, THEME.button.primary);
      this.roastCollectText.x = btnX + btnW / 2;
      this.roastCollectText.y = btnY + btnH / 2;
    } else {
      this.roastLabelText.visible = true;
      this.roastResultContainer.visible = false;
    }
  }

  private updateBoostButtons(state: GameState): void {
    const tapActive = state.playTime < state.boosts.tapEndTime;
    const bpsActive = state.playTime < state.boosts.bpsEndTime;

    const maxBoostW = 140;
    const btnW = Math.min(maxBoostW, (this.screenW - LAYOUT.padding * 3) / 2);
    const btnH = LAYOUT.boostBarH;

    this.tapBoostGfx.clear();
    drawPillButton(
      this.tapBoostGfx,
      0, 0, btnW, btnH,
      tapActive ? THEME.accent.orange : THEME.button.boost,
      tapActive ? 0.5 : 0.8
    );

    this.bpsBoostGfx.clear();
    drawPillButton(
      this.bpsBoostGfx,
      0, 0, btnW, btnH,
      bpsActive ? THEME.accent.orange : THEME.button.boost,
      bpsActive ? 0.5 : 0.8
    );
  }

  // ═══════════════════════════════════════════════
  //  Resize
  // ═══════════════════════════════════════════════

  resize(): void {
    this.screenW = this.app.screen.width;
    this.screenH = this.app.screen.height;
    const w = this.screenW;
    const h = this.screenH;
    const pad = LAYOUT.padding;
    const safeTop = LAYOUT.safeTop;
    const safeBottom = LAYOUT.safeBottom;

    // ── Background ──
    if (this.bgSprite) {
      const scaleX = w / this.bgSprite.texture.width;
      const scaleY = h / this.bgSprite.texture.height;
      const scale = Math.max(scaleX, scaleY);
      this.bgSprite.scale.set(scale);
      this.bgSprite.x = (w - this.bgSprite.texture.width * scale) / 2;
      this.bgSprite.y = (h - this.bgSprite.texture.height * scale) / 2;
    }
    this.bgOverlay.clear();
    this.bgOverlay.beginFill(0x0D0704, 0.72);
    this.bgOverlay.drawRect(0, 0, w, h);
    this.bgOverlay.endFill();

    // ── Top bar ──
    this.topBarGfx.clear();
    this.topBarGfx.beginFill(THEME.bg.panel, 0.85);
    this.topBarGfx.drawRect(0, 0, w, LAYOUT.topBarH + safeTop);
    this.topBarGfx.endFill();

    const topY = safeTop + LAYOUT.topBarH / 2;
    this.beanCountText.x = pad + 8;
    this.beanCountText.y = topY - 8;
    this.bpsText.x = pad + 8;
    this.bpsText.y = topY + 16;
    this.goldenBeanText.x = w - pad - 8;
    this.goldenBeanText.y = topY - 8;

    // Stage badge (top right area)
    const badgeW = 80;
    const badgeH = 22;
    const badgeX = w - pad - 8 - badgeW;
    const badgeY = topY + 6;
    this.stageBadgeGfx.clear();
    drawPillButton(this.stageBadgeGfx, badgeX, badgeY, badgeW, badgeH, THEME.accent.brown, 0.6);
    this.stageBadgeText.x = badgeX + badgeW / 2;
    this.stageBadgeText.y = badgeY + badgeH / 2;

    // ── Tab bar ──
    const tabBarY = h - LAYOUT.tabBarH - safeBottom;
    this.tabBarGfx.clear();
    this.tabBarGfx.beginFill(THEME.bg.panel, 0.9);
    this.tabBarGfx.drawRect(0, tabBarY, w, LAYOUT.tabBarH + safeBottom);
    this.tabBarGfx.endFill();

    const tabW = w / TAB_EMOJIS.length;
    for (let i = 0; i < this.tabTexts.length; i++) {
      this.tabTexts[i].x = tabW * i + tabW / 2;
      this.tabTexts[i].y = tabBarY + LAYOUT.tabBarH / 2;
    }
    this.drawTabIndicator();

    // ── Content area ──
    const contentTop = LAYOUT.topBarH + safeTop;
    const contentBottom = tabBarY;
    const contentH = contentBottom - contentTop;

    // ── Tap area (centered in content) ──
    const tapCenterY = contentTop + contentH * 0.35;
    this.tapContainer.x = w / 2;
    this.tapContainer.y = tapCenterY;

    if (this.beanSprite) {
      const maxBeanSize = Math.min(w * 0.35, contentH * 0.25);
      this.beanBaseScale = maxBeanSize / Math.max(this.beanSprite.texture.width, this.beanSprite.texture.height);
      this.beanSprite.scale.set(this.beanBaseScale);
    }

    this.tapHintText.y = 80;

    // ── Combo (top-left of content) ──
    this.comboContainer.x = pad + 8;
    this.comboContainer.y = contentTop + 12;
    this.comboTierText.x = 0;
    this.comboTierText.y = 24;

    // ── Boost buttons ──
    const boostY = tapCenterY + 110;
    const maxBoostW = 140;
    const boostBtnW = Math.min(maxBoostW, (w - pad * 3) / 2);
    const boostTotalW = boostBtnW * 2 + pad;
    this.boostContainer.x = (w - boostTotalW) / 2;
    this.boostContainer.y = boostY;
    this.tapBoostGfx.x = 0;
    this.tapBoostText.x = boostBtnW / 2;
    this.tapBoostText.y = LAYOUT.boostBarH / 2;
    this.bpsBoostGfx.x = boostBtnW + pad;
    this.bpsBoostText.x = boostBtnW + pad + boostBtnW / 2;
    this.bpsBoostText.y = LAYOUT.boostBarH / 2;

    // ── Panels ──
    const panelTop = contentTop + 4;
    const panelH = this.getPanelHeight();
    this.panelContainer.y = panelTop;

    this.producerPanel.onResize(w, panelH);
    this.achievementPanel.onResize(w, panelH);
    this.prestigePanel.onResize(w, panelH);
    this.settingsPanel.onResize(w, panelH);

    // ── Popup manager ──
    this.popupManager.resize(w);

    // ── Roasting overlay dim ──
    const dimBg = this.roastOverlay.children[0] as PIXI.Graphics;
    if (dimBg) {
      dimBg.clear();
      dimBg.beginFill(0x000000, 0.7);
      dimBg.drawRect(0, 0, w, h);
      dimBg.endFill();
    }
    // Roasting tap area
    const tapAreaRoast = this.roastOverlay.children[3] as PIXI.Graphics | undefined;
    if (tapAreaRoast && tapAreaRoast instanceof PIXI.Graphics) {
      tapAreaRoast.clear();
      tapAreaRoast.beginFill(0x000000, 0.001);
      tapAreaRoast.drawRect(0, 0, w, h);
      tapAreaRoast.endFill();
    }
  }

  private getPanelHeight(): number {
    const safeTop = LAYOUT.safeTop;
    const safeBottom = LAYOUT.safeBottom;
    const contentTop = LAYOUT.topBarH + safeTop + 4;
    const contentBottom = this.screenH - LAYOUT.tabBarH - safeBottom;
    return contentBottom - contentTop;
  }
}
